import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const FinalOutput = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { imageData, videoFrameData: initialVideoFrameData, sessionId: stateSessionId, frameName, uploadStatus: initialUploadStatus, selectedFilter: initialSelectedFilter } = location.state || {};

  // Ambil selectedFilter dari state, default 'normal'
  const [selectedFilter] = useState(initialSelectedFilter || 'normal');

  // Debug: Log received data
  console.log('FinalOutput received data:', {
    hasImageData: !!imageData,
    hasVideoFrameData: !!initialVideoFrameData,
    videoFrameDataUrl: initialVideoFrameData,
    videoFrameDataType: initialVideoFrameData ? (initialVideoFrameData.includes('uploads') ? 'original-video' : 'generated-frame') : 'none',
    sessionId,
    frameName,
    stateSessionId,
    uploadStatus: initialUploadStatus
  });

  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoFrameData, setVideoFrameData] = useState(initialVideoFrameData);
  const [uploadStatus, setUploadStatus] = useState(initialUploadStatus || 'idle'); // 'idle', 'uploading', 'success', 'error'
  const videoRef = useRef(null);
  const [videoDownloadLoading, setVideoDownloadLoading] = useState(false);

  const API_BASE = 'https://api.laviopaperie.com';
  function joinUrl(base, path) {
    if (!base.endsWith('/') && !path.startsWith('/')) return base + '/' + path;
    if (base.endsWith('/') && path.startsWith('/')) return base + path.slice(1);
    return base + path;
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'ArrowLeft') {
        setCurrentSlide(prev => Math.max(0, prev - 1));
      } else if (event.key === 'ArrowRight') {
        setCurrentSlide(prev => Math.min((sessionData?.files?.length || 0), prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [sessionData]);

  // Auto-play videos when slide changes with improved smoothness
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const videoElements = document.querySelectorAll('video');
      console.log('Found video elements:', videoElements.length);
      videoElements.forEach((video, index) => {
        console.log(`Video ${index}:`, {
          src: video.src,
          paused: video.paused,
          readyState: video.readyState,
          currentTime: video.currentTime,
          duration: video.duration
        });

        // Set video properties for smoother playback
        video.muted = true;
        video.playsInline = true;
        video.loop = true;
        video.preload = 'auto';

        // Try to play video with better error handling
        if (video.readyState >= 2) {
          console.log(`Attempting to play video ${index}`);
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              // Autoplay failed, user needs to click play
              console.log('Autoplay failed for video:', video.src, error);
            });
          }
        } else {
          // If video is not ready, wait for it to load
          video.addEventListener('canplay', () => {
            console.log(`Video ${index} can play, attempting to start`);
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                console.log('Canplay autoplay failed:', error);
              });
            }
          }, { once: true });
        }
      });
    }, 800); // Longer delay to ensure DOM and video are ready

    return () => clearTimeout(timeoutId);
  }, [currentSlide]);

  // Add global CSS to prevent video flickering and improve smoothness
  useEffect(() => {
    // Load cursive font for the decorative title
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(fontLink);

    const style = document.createElement('style');
    style.textContent = `
      video {
        -webkit-backface-visibility: hidden !important;
        backface-visibility: hidden !important;
        -webkit-transform: translate3d(0, 0, 0) !important;
        transform: translate3d(0, 0, 0) !important;
        -webkit-filter: none !important;
        filter: none !important;
        will-change: auto !important;
        image-rendering: -webkit-optimize-contrast !important;
        image-rendering: crisp-edges !important;
        -webkit-font-smoothing: antialiased !important;
        -moz-osx-font-smoothing: grayscale !important;
      }
      canvas {
        image-rendering: -webkit-optimize-contrast !important;
        image-rendering: crisp-edges !important;
        -webkit-font-smoothing: antialiased !important;
        -moz-osx-font-smoothing: grayscale !important;
      }
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(6px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes softPulse {
        0% { box-shadow: 0 0 0 0 rgba(255,182,193,0.4); }
        70% { box-shadow: 0 0 0 15px rgba(255,182,193,0); }
        100% { box-shadow: 0 0 0 0 rgba(255,182,193,0); }
      }
      @keyframes rotateSlow {
        0% { transform: translate(-50%, -50%) rotate(0deg); }
        100% { transform: translate(-50%, -50%) rotate(360deg); }
      }
      @keyframes gradientShift2 {
        0% { background-position: 0% 0%; }
        50% { background-position: 100% 100%; }
        100% { background-position: 0% 0%; }
      }
      /* Animated title shimmer and gentle float */
      @keyframes textShimmer {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes textFloat {
        0% { transform: translateY(0); }
        50% { transform: translateY(-3px); }
        100% { transform: translateY(0); }
      }
      @keyframes titleWave {
        0% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
        100% { transform: translateY(0); }
      }
      /* Enhanced animated pastel background helpers */
      @keyframes blobFloatA {
        0% { transform: translate(0, 0) scale(1) rotate(0deg); }
        33% { transform: translate(25px, -20px) scale(1.05) rotate(120deg); }
        66% { transform: translate(-15px, 15px) scale(0.95) rotate(240deg); }
        100% { transform: translate(0, 0) scale(1) rotate(360deg); }
      }
      @keyframes blobFloatB {
        0% { transform: translate(0, 0) scale(1) rotate(0deg); }
        33% { transform: translate(-30px, 25px) scale(1.08) rotate(-120deg); }
        66% { transform: translate(20px, -10px) scale(0.92) rotate(-240deg); }
        100% { transform: translate(0, 0) scale(1) rotate(-360deg); }
      }
      @keyframes blobFloatC {
        0% { transform: translate(0, 0) scale(1) rotate(0deg); }
        33% { transform: translate(15px, 30px) scale(0.96) rotate(180deg); }
        66% { transform: translate(-25px, -5px) scale(1.04) rotate(300deg); }
        100% { transform: translate(0, 0) scale(1) rotate(360deg); }
      }
      /* Soft breathing animation */
      @keyframes softBreathing {
        0% { transform: scale(1); opacity: 0.8; }
        50% { transform: scale(1.05); opacity: 1; }
        100% { transform: scale(1); opacity: 0.8; }
      }
      /* Gentle sway animation */
      @keyframes gentleSway {
        0% { transform: translateX(0) rotate(0deg); }
        25% { transform: translateX(5px) rotate(1deg); }
        75% { transform: translateX(-5px) rotate(-1deg); }
        100% { transform: translateX(0) rotate(0deg); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(fontLink)) {
        document.head.removeChild(fontLink);
      }
    };
  }, []);

  // Fetch session data (photos and videos) based on sessionId
  useEffect(() => {
    const fetchSessionData = async () => {
      if (!sessionId) return;

      try {
        setLoading(true);

        // First try to get session files from API
        const sessionResponse = await fetch(joinUrl(API_BASE, `/api/session-files/${sessionId}`));
        console.log('API response status:', sessionResponse.status);

        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          console.log('Session files from API:', sessionData);

          // Transform API data to match expected format
          const transformedData = {
            files: sessionData.files.map(file => ({
              filename: file.filename,
              path: joinUrl(API_BASE, file.path),
              type: file.type,
              size: file.size
            }))
          };

          console.log('Transformed session data:', transformedData);
          setSessionData(transformedData);
          setLoading(false);
          return;
        } else {
          console.log('API response not ok, status:', sessionResponse.status);
          const errorText = await sessionResponse.text();
          console.log('API error response:', errorText);
        }

        // Fallback to old check-files endpoint
        const response = await fetch(joinUrl(API_BASE, `/api/check-files/${sessionId}`));
        if (!response.ok) {
          throw new Error('Session tidak ditemukan');
        }
        const data = await response.json();
        setSessionData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId]);

  const downloadSessionFile = async (filename) => {
    try {
      // Jika file .webm, langsung konversi ke mp4
      if (filename.endsWith('.webm')) {
        try {
          const responseMp4 = await fetch(joinUrl(API_BASE, `/api/convert-to-mp4?filename=${encodeURIComponent(filename)}`));
          if (responseMp4.ok) {
            const mp4Blob = await responseMp4.blob();
            const url = window.URL.createObjectURL(mp4Blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename.replace(/\.webm$/, '.mp4');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            return;
          } else {
            console.warn('Konversi mp4 gagal, fallback ke webm');
          }
        } catch (err) {
          console.warn('Error konversi mp4, fallback webm:', err);
        }
      }
      // Normal fetch as before
      const response = await fetch(joinUrl(API_BASE, `/uploads/${filename}`));
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();

      // Check if it's an image or video that needs mirroring
      const isImage = filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.png');
      const isVideo = filename.endsWith('.webm') || filename.endsWith('.mp4');

      if (isImage) {
        // Create mirrored version for images
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        const imageUrl = window.URL.createObjectURL(blob);

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;

          // Mirror the image horizontally
          ctx.scale(-1, 1);
          ctx.drawImage(img, -img.width, 0);

          // Convert to blob and download
          canvas.toBlob((mirroredBlob) => {
            const url = window.URL.createObjectURL(mirroredBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `mirrored_${filename}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            window.URL.revokeObjectURL(imageUrl);
          }, 'image/jpeg', 0.9);
        };

        img.src = imageUrl;
      } else if (isVideo) {
        // For videos, convert to MP4 if it's a .webm file
        if (filename.endsWith('.webm')) {
          try {
            // Call backend to convert and download as MP4
            const response = await fetch(joinUrl(API_BASE, `/api/convert-to-mp4?filename=${encodeURIComponent(filename)}`));
            if (response.ok) {
              const mp4Blob = await response.blob();
              const url = window.URL.createObjectURL(mp4Blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename.replace(/\.webm$/, '.mp4');
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            } else {
              throw new Error('MP4 conversion failed');
            }
          } catch (error) {
            console.warn(`MP4 conversion failed for ${filename}, downloading original webm:`, error);
            // Fallback to original webm if conversion fails
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }
        } else {
          // For MP4 files, download as is
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } else {
        // For other files, download as is
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  // Create mirrored version of video - robust, ensures non-zero output (same as CameraResults)
  const createMirroredVideo = async (videoBlob) => {
    return new Promise((resolve, reject) => {
      try {
        const videoUrl = URL.createObjectURL(videoBlob);
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = 'anonymous';

        let mediaRecorder = null;
        let stream = null;
        const chunks = [];
        let frameCount = 0;
        let stopped = false;

        const finalize = () => {
          try {
            URL.revokeObjectURL(videoUrl);
            const type = (mediaRecorder && mediaRecorder.mimeType) || 'video/webm';
            if (chunks.length === 0 || frameCount === 0) {
              reject(new Error('No video data recorded'));
              return;
            }
            const mirroredBlob = new Blob(chunks, { type });
            resolve(mirroredBlob);
          } catch (e) {
            reject(e);
          }
        };

        const stopRecording = () => {
          if (stopped) return;
          stopped = true;
          try {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
              // Flush last data and stop shortly after
              mediaRecorder.requestData();
              setTimeout(() => {
                try { mediaRecorder.stop(); } catch (_) { finalize(); }
              }, 80);
            } else {
              finalize();
            }
          } catch (_) {
            finalize();
          }
        };

        video.onloadedmetadata = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            stream = canvas.captureStream(30);

            const mimeCandidates = [
              'video/webm;codecs=vp9',
              'video/webm;codecs=vp8',
              'video/webm'
            ];
            const selected =
              mimeCandidates.find(t => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) ||
              'video/webm';

            mediaRecorder = new MediaRecorder(stream, { mimeType: selected });

            mediaRecorder.ondataavailable = (e) => {
              if (e.data && e.data.size > 0) chunks.push(e.data);
            };
            mediaRecorder.onstop = finalize;
            mediaRecorder.onerror = (ev) => reject(ev.error || new Error('MediaRecorder error'));

            // Start with timeslice to ensure non-empty chunks
            mediaRecorder.start(100);

            const draw = () => {
              if (video.ended) return;
              // Draw only when ready, but do not stop on buffering/pause
              if (video.readyState >= 2 && !video.paused) {
                ctx.save();
                ctx.scale(-1, 1);
                ctx.translate(-canvas.width, 0);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                ctx.restore();
                frameCount++;
              }
              requestAnimationFrame(draw);
            };

            video.onended = stopRecording;

            const playPromise = video.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise.then(() => {
                draw();
              }).catch(err => reject(err));
            } else {
              draw();
            }
          } catch (e) {
            reject(e);
          }
        };

        video.onerror = () => reject(new Error('Failed to load video'));
        video.src = videoUrl;
        video.load();
      } catch (e) {
        reject(e);
      }
    });
  };

  // Helper to get CSS filter string from selectedFilter
  const getCssFilter = (filter) => {
    switch (filter) {
      case 'bnw': return 'grayscale(1)';
      case 'sepia': return 'sepia(1)';
      case 'cool': return 'contrast(1.1) brightness(1.05) hue-rotate(-20deg)';
      case 'warm': return 'contrast(1.1) brightness(1.08) sepia(0.3) hue-rotate(20deg)';
      default: return 'none';
    }
  };

  // Show loading while fetching data from API
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        <h2>Loading...</h2>
        <p>Fetching session data from server...</p>
      </div>
    );
  }

  // Show error if no data found from API
  if (error || (!sessionData && !imageData)) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        <h2>Error</h2>
        <p>{error || 'Session not found. Please check the URL or go back and try again.'}</p>
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            onClick={() => navigate('/camera-session')}
            style={{
              padding: '10px 20px',
              background: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Back to Camera Session
          </button>
          <button
            onClick={() => navigate('/frames')}
            style={{
              padding: '10px 20px',
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Choose Frame
          </button>
        </div>
      </div>
    );
  }

  const downloadImage = async () => {
    const finalFrameFiles = getFinalFrameFiles();
    const imageUrl = finalFrameFiles.image || imageData;

    if (!imageUrl) {
      alert('No image available for download');
      return;
    }

    try {
      // Fetch the image as blob
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `session-${sessionId || 'output'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download image');
    }
  };

  const downloadVideoFrame = async () => {
    const finalFrameFiles = getFinalFrameFiles();
    const videoUrl = finalFrameFiles.video || videoFrameData;
    if (!videoUrl) {
      alert('No video available for download');
      return;
    }
    try {
      setVideoDownloadLoading(true);
      
      // Extract filename from URL if it's from uploads
      let filename = '';
      let isWebmFromUploads = false;
      
      if (videoUrl.includes('/uploads/')) {
        // Extract filename from uploads URL
        const urlParts = videoUrl.split('/uploads/');
        if (urlParts.length > 1) {
          filename = urlParts[1].split('?')[0]; // Remove query params if any
          isWebmFromUploads = filename.endsWith('.webm');
        }
      } else if (videoUrl.includes('final_frame') && (videoUrl.endsWith('.webm') || videoUrl.includes('.webm'))) {
        // Try to extract filename from URL pattern like sessionId_final_frame_video.webm
        const match = videoUrl.match(/([^/]+\.webm)/);
        if (match) {
          filename = match[1];
          isWebmFromUploads = true;
        }
      }
      
      // If it's a webm file from uploads, convert to mp4 via backend
      if (isWebmFromUploads && filename) {
        try {
          // Call backend to convert and stream mp4
          const response = await fetch(joinUrl(API_BASE, `/api/convert-to-mp4?filename=${encodeURIComponent(filename)}`));
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `final-frame-${sessionId || 'output'}.mp4`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setVideoDownloadLoading(false);
            return;
          } else {
            // If conversion fails, log and try fallback
            const errorText = await response.text();
            console.warn('MP4 conversion failed:', errorText);
          }
        } catch (conversionError) {
          console.warn('Error during MP4 conversion:', conversionError);
          // Continue to fallback
        }
      }
      
      // Fallback: For blob URLs, data URLs, or if conversion failed
      // Check if it's a blob URL or data URL that needs to be uploaded first
      const isBlobUrl = videoUrl.startsWith('blob:');
      const isDataUrl = videoUrl.startsWith('data:');
      
      if (isBlobUrl || isDataUrl) {
        // For blob/data URLs, fetch and upload first, then convert
        try {
          const response = await fetch(videoUrl);
          if (!response.ok) throw new Error('Failed to fetch video');
          const blob = await response.blob();
          
          // Only upload if it's webm
          if (blob.type === 'video/webm' || videoUrl.includes('.webm')) {
            // Upload the blob to backend first
            const formData = new FormData();
            const tempFilename = `${sessionId || 'temp'}_final_frame_video_${Date.now()}.webm`;
            formData.append('files', blob, tempFilename);
            formData.append('sessionId', sessionId || 'temp');
            
            const uploadResponse = await fetch(joinUrl(API_BASE, '/api/upload-session'), {
              method: 'POST',
              body: formData
            });
            
            if (uploadResponse.ok) {
              // Wait a bit for file to be saved
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Now convert the uploaded file
              const convertResponse = await fetch(joinUrl(API_BASE, `/api/convert-to-mp4?filename=${encodeURIComponent(tempFilename)}`));
              if (convertResponse.ok) {
                const mp4Blob = await convertResponse.blob();
                const url = window.URL.createObjectURL(mp4Blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `final-frame-${sessionId || 'output'}.mp4`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                setVideoDownloadLoading(false);
                return;
              }
            }
          }
        } catch (uploadError) {
          console.warn('Upload and convert failed, downloading as webm:', uploadError);
        }
      }
      
      // Final fallback: fetch and download as-is
      const response = await fetch(videoUrl);
      if (!response.ok) throw new Error('Failed to fetch video');
      const blob = await response.blob();
      
      // Final fallback: download as-is (but prefer mp4 extension if available)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Always use .mp4 extension for final frame video
      link.download = `final-frame-${sessionId || 'output'}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setVideoDownloadLoading(false);
    } catch (error) {
      setVideoDownloadLoading(false);
      console.error('Download error:', error);
      alert('Failed to download video');
    }
  };

  // Function to get final frame files from session data
  const getFinalFrameFiles = () => {
    // Priority 1: Use API data if available
    if (sessionData && sessionData.files) {
      const finalFrameImage = sessionData.files.find(file =>
        file.filename.includes('final_frame') &&
        (file.filename.endsWith('.png') || file.filename.endsWith('.jpg') || file.filename.endsWith('.jpeg'))
      );

      const finalFrameVideoWebm = sessionData.files.find(file =>
        file.filename.includes('final_frame') &&
        file.filename.endsWith('.webm')
      );

      const finalFrameVideoMp4 = sessionData.files.find(file =>
        file.filename.includes('final_frame') &&
        file.filename.endsWith('.mp4')
      );

      return {
        image: finalFrameImage ? finalFrameImage.path : null,
        video: finalFrameVideoWebm ? finalFrameVideoWebm.path : (finalFrameVideoMp4 ? finalFrameVideoMp4.path : null)
      };
    }

    // Priority 2: Fallback to location.state data
    return {
      image: imageData || null,
      video: videoFrameData || null
    };
  };

  const startNewSession = () => {
    // Clean up current session data
    const sessionFrames = JSON.parse(localStorage.getItem('sessionFrames') || '{}');
    delete sessionFrames[sessionId];
    localStorage.setItem('sessionFrames', JSON.stringify(sessionFrames));
    localStorage.removeItem('currentSessionId');

    navigate('/camera-session');
  };

  // Function to manually upload final frame files
  const uploadFinalFrameFiles = async () => {
    if (!imageData && !videoFrameData) {
      alert('No final frame files to upload');
      return;
    }

    setUploadStatus('uploading');

    try {
      const formData = new FormData();
      formData.append('sessionId', sessionId);

      // Convert image data URL to blob and add to form data
      if (imageData) {
        const imageResponse = await fetch(imageData);
        const imageBlob = await imageResponse.blob();
        formData.append('files', imageBlob, `${sessionId}_final_frame.png`);
      }

      // Convert video blob URL to blob and add to form data
      if (videoFrameData) {
        const videoResponse = await fetch(videoFrameData);
        const videoBlob = await videoResponse.blob();
        formData.append('files', videoBlob, `${sessionId}_final_frame_video.webm`);
      }

      const response = await fetch(joinUrl(API_BASE, '/api/upload-session'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      const result = await response.json();
      console.log('Final frame files uploaded successfully:', result);
      setUploadStatus('success');

      // Refresh session data to show uploaded files
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Error uploading final frame files:', error);
      setUploadStatus('error');
      alert('Failed to upload final frame files: ' + error.message);
    }
  };

  return (
    <div style={{
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      background: 'linear-gradient(135deg, #ffffff, #f6fffb, #fff4ee)',
      backgroundSize: '200% 200%',
      animation: 'gradientShift 16s ease infinite',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Enhanced pastel floating blobs & dynamic layers */}
      <div style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0
      }}>
        {/* Main animated gradient background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, #f8f4ff 0%, #fff0f5 25%, #f0f8ff 50%, #fff8f0 75%, #f8f4ff 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 20s ease-in-out infinite'
        }} />
        
        {/* Rotating conic gradient ring */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '1800px',
          height: '1800px',
          transform: 'translate(-50%, -50%)',
          background: 'conic-gradient(from 0deg, rgba(255,182,193,0.15), rgba(173,216,230,0.15), rgba(255,218,185,0.15), rgba(221,160,221,0.15), rgba(255,182,193,0.15))',
          borderRadius: '50%',
          filter: 'blur(40px)',
          animation: 'rotateSlow 35s linear infinite'
        }} />
        
        {/* Animated soft gradient overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(120deg, rgba(255,182,193,0.3), rgba(173,216,230,0.3), rgba(255,218,185,0.3), rgba(221,160,221,0.3))',
          backgroundSize: '300% 300%',
          animation: 'gradientShift2 12s ease-in-out infinite'
        }} />
        
        {/* Large floating blobs */}
        <div style={{
          position: 'absolute',
          top: '-100px',
          left: '-80px',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle at 30% 30%, rgba(255,182,193,0.6), rgba(255,182,193,0.1), transparent)',
          filter: 'blur(25px)',
          animation: 'blobFloatA 15s ease-in-out infinite'
        }} />
        
        <div style={{
          position: 'absolute',
          bottom: '-120px',
          right: '-90px',
          width: '450px',
          height: '450px',
          background: 'radial-gradient(circle at 70% 70%, rgba(173,216,230,0.6), rgba(173,216,230,0.1), transparent)',
          filter: 'blur(28px)',
          animation: 'blobFloatB 18s ease-in-out infinite'
        }} />
        
        <div style={{
          position: 'absolute',
          top: '25%',
          left: '65%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle at 50% 50%, rgba(255,218,185,0.7), rgba(255,218,185,0.1), transparent)',
          filter: 'blur(22px)',
          transform: 'translate(-50%, -50%)',
          animation: 'blobFloatC 20s ease-in-out infinite'
        }} />
        
        {/* Medium floating blobs */}
        <div style={{
          position: 'absolute',
          top: '15%',
          right: '20%',
          width: '220px',
          height: '220px',
          background: 'radial-gradient(circle at 40% 40%, rgba(221,160,221,0.5), rgba(221,160,221,0.1), transparent)',
          filter: 'blur(20px)',
          animation: 'blobFloatA 16s ease-in-out infinite'
        }} />
        
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '15%',
          width: '250px',
          height: '250px',
          background: 'radial-gradient(circle at 60% 60%, rgba(255,228,225,0.6), rgba(255,228,225,0.1), transparent)',
          filter: 'blur(18px)',
          animation: 'blobFloatB 22s ease-in-out infinite'
        }} />
        
        <div style={{
          position: 'absolute',
          top: '70%',
          right: '40%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle at 50% 50%, rgba(176,224,230,0.6), rgba(176,224,230,0.1), transparent)',
          filter: 'blur(16px)',
          animation: 'blobFloatC 19s ease-in-out infinite'
        }} />
        
        {/* Small decorative blobs */}
        <div style={{
          position: 'absolute',
          top: '45%',
          left: '10%',
          width: '120px',
          height: '120px',
          background: 'radial-gradient(circle at 50% 50%, rgba(255,192,203,0.4), transparent)',
          filter: 'blur(12px)',
          animation: 'blobFloatA 14s ease-in-out infinite'
        }} />
        
        <div style={{
          position: 'absolute',
          top: '60%',
          left: '80%',
          width: '100px',
          height: '100px',
          background: 'radial-gradient(circle at 50% 50%, rgba(230,230,250,0.5), transparent)',
          filter: 'blur(10px)',
          animation: 'blobFloatB 17s ease-in-out infinite'
        }} />
        
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '40%',
          width: '80px',
          height: '80px',
          background: 'radial-gradient(circle at 50% 50%, rgba(255,218,185,0.4), transparent)',
          filter: 'blur(8px)',
          animation: 'blobFloatC 13s ease-in-out infinite'
        }} />
      </div>
      
      {/* Enhanced dot pattern overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(rgba(147, 112, 219, 0.1) 1.5px, transparent 1.5px), radial-gradient(rgba(255, 182, 193, 0.08) 1px, transparent 1px)',
        backgroundSize: '32px 32px, 24px 24px',
        backgroundPosition: '0 0, 16px 16px',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      {uploadStatus === 'uploading' && (
        <div style={{
          textAlign: 'center',
          marginBottom: '15px',
          padding: '10px',
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '6px',
          color: '#856404'
        }}>
          ⏳ Uploading final frame files to server...
        </div>
      )}

      {uploadStatus === 'success' && (
        <div style={{
          textAlign: 'center',
          marginBottom: '15px',
          padding: '10px',
          background: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '6px',
          color: '#155724'
        }}>
          ✅ Final frame files uploaded successfully!
        </div>
      )}

      {uploadStatus === 'error' && (
        <div style={{
          textAlign: 'center',
          marginBottom: '15px',
          padding: '10px',
          background: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '6px',
          color: '#721c24'
        }}>
          ❌ Failed to upload final frame files
        </div>
      )}


      {/* Combined Slide View */}
      <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            Loading session data...
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
            Error: {error}
          </div>
        )}

        {(sessionData || imageData || videoFrameData) && (() => {
          // Get final frame files from API
          const finalFrameFiles = getFinalFrameFiles();
          console.log('Final frame files:', finalFrameFiles);

          // Create slides array: Final Frame + Video Frame + Session Files
          const slides = [
            // Final frame image from API or fallback to state
            ...(finalFrameFiles.image ? [{
              type: 'final-frame',
              content: finalFrameFiles.image,
              title: 'Final Frame',
              filename: `session-${sessionId}.png`
            }] : []),
            // Final frame video from API or fallback to state
            ...(finalFrameFiles.video ? [{
              type: 'video-frame',
              content: finalFrameFiles.video,
              title: 'Final Frame Video (5s)',
              filename: `final-frame-${sessionId}.webm`
            }] : []),
            // Other session files (prefer .webm when both exist)
            ...(sessionData.files || []).map(file => {
              const isVideo = file.filename.includes('video') || file.filename.endsWith('.webm') || file.filename.endsWith('.mp4');
              const isPhoto = file.filename.endsWith('.jpg') || file.filename.endsWith('.jpeg') || file.filename.endsWith('.png');
              const isFinalFrame = file.filename.includes('final_frame');
              const isMp4 = file.filename.endsWith('.mp4');
              const webmExists = isMp4
                ? sessionData.files.some(candidate =>
                    candidate.filename === file.filename.replace(/\.mp4$/, '.webm')
                  )
                : false;

              // Skip final frame files as they're already shown above
              if (isFinalFrame) return null;
              // Skip mp4 if a matching webm exists (keep sources as webm)
              if (isVideo && isMp4 && webmExists) return null;

              return {
                type: isVideo ? 'video' : isPhoto ? 'photo' : 'file',
                content: file.path,
                title: isVideo ? 'Video' : isPhoto ? 'Photo' : 'File',
                filename: file.filename
              };
            }).filter(Boolean) // Remove null entries
          ];

          // If no slides from API, try to use location.state data
          if (slides.length === 0 && (imageData || videoFrameData)) {
            const fallbackSlides = [];

            if (imageData) {
              fallbackSlides.push({
                type: 'final-frame',
                content: imageData,
                title: 'Final Frame (Local)',
                filename: `session-${sessionId}.png`
              });
            }

            if (videoFrameData) {
              fallbackSlides.push({
                type: 'video-frame',
                content: videoFrameData,
                title: 'Final Frame Video (5s) (Local)',
                filename: `final-frame-${sessionId}.webm`
              });
            }

            return fallbackSlides;
          }

          // Debug: Log slides information
          console.log('Slides created:', slides.map(slide => ({
            type: slide.type,
            title: slide.title,
            hasContent: !!slide.content,
            contentType: slide.content ? (slide.content.startsWith('blob:') ? 'blob' : slide.content.startsWith('data:') ? 'data-url' : 'url') : 'none'
          })));

          const totalSlides = slides.length;
          const currentSlideData = slides[currentSlide] || slides[0];

          return (
            <div style={{
              background: 'rgba(255,255,255,0.66)',
              backdropFilter: 'blur(4px)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 10px 24px rgba(204, 180, 214, 0.25)',
              border: '1px solid rgba(204, 180, 214, 0.3)'
            }}>
              {/* Slide Counter */}
              {/* <div style={{
                textAlign: 'center',
                marginBottom: '15px',
                color: '#6c757d',
                fontSize: '14px'
              }}>
                {currentSlide + 1} / {totalSlides} - {currentSlideData.title}
              </div> */}

              {/* Decorative title */}
              <div style={{
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                <div style={{
                  fontFamily: `'Great Vibes', cursive`,
                  fontSize: '50px',
                  lineHeight: 1.1,
                  letterSpacing: '1px',
                  color: '#8f72a6',
                  textShadow: '0 1px 0 rgba(255,255,255,0.6)'
                }}>
                  {Array.from('-  Lavio Paperie - ').map((ch, i) => (
                    <span
                      key={i}
                      style={{
                        display: 'inline-block',
                        animation: 'titleWave 2.2s ease-in-out infinite',
                        animationDelay: `${i * 0.06}s`,
                        willChange: 'transform'
                      }}
                    >
                       {ch === ' ' ? '\u00A0' : ch}
                    </span>
                  ))}
                </div>
              </div>
                {/* Small centered note */}
                <div style={{
                textAlign: 'center',
                marginTop: '10px',
                marginBottom: '8px',
                color: '#000',
                fontSize: '12px'
              }}>
                Thank you, dont forget to save it! the photos and video are only available for 3 days after the take✨
              </div>

              {/* Main Slide Display */}
              <div style={{
                position: 'relative',
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {/* Left Navigation Button */}
                <button
                  onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                  disabled={currentSlide === 0}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '50px',
                    height: '50px',
                    background: currentSlide === 0 ? 'rgba(233, 236, 239, 0.7)' : '#ccb4d6',
                    color: currentSlide === 0 ? '#6c757d' : '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    transition: 'all 0.3s ease',
                    boxShadow: '0 6px 16px rgba(204, 180, 214, 0.35)'
                  }}
                  onMouseEnter={(e) => {
                    if (currentSlide !== 0) {
                      e.target.style.background = '#bca0cd';
                      e.target.style.transform = 'translateY(-50%) scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSlide !== 0) {
                      e.target.style.background = '#ccb4d6';
                      e.target.style.transform = 'translateY(-50%) scale(1)';
                    }
                  }}
                >
                  ‹
                </button>

                {/* Right Navigation Button */}
                <button
                  onClick={() => setCurrentSlide(Math.min(totalSlides - 1, currentSlide + 1))}
                  disabled={currentSlide === totalSlides - 1}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '50px',
                    height: '50px',
                    background: currentSlide === totalSlides - 1 ? 'rgba(233, 236, 239, 0.7)' : '#ccb4d6',
                    color: currentSlide === totalSlides - 1 ? '#6c757d' : '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: currentSlide === totalSlides - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    transition: 'all 0.3s ease',
                    boxShadow: '0 6px 16px rgba(204, 180, 214, 0.35)'
                  }}
                  onMouseEnter={(e) => {
                    if (currentSlide !== totalSlides - 1) {
                      e.target.style.background = '#bca0cd';
                      e.target.style.transform = 'translateY(-50%) scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSlide !== totalSlides - 1) {
                      e.target.style.background = '#ccb4d6';
                      e.target.style.transform = 'translateY(-50%) scale(1)';
                    }
                  }}
                >
                  ›
                </button>

                <div style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  {currentSlideData.type === 'final-frame' ? (
                    <img
                      src={currentSlideData.content}
                      alt="Final Frame"
                      style={{
                        width: 'auto',
                        maxWidth: 'auto',
                        height: '375px',
                        borderRadius: '8px',
                        background: 'transparent',
                        boxShadow: '0 10px 24px rgba(204, 180, 214, 0.25)'
                      }}
                    />
                  ) : currentSlideData.type === 'photo' ? (
                    <img
                      src={currentSlideData.content}
                      alt={`Photo`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxWidth: '400px',
                        maxHeight: '55vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        transform: 'scaleX(-1)',
                        boxShadow: '0 10px 24px rgba(204, 180, 214, 0.25)'
                      }}
                    />
                  ) : currentSlideData.type === 'video-frame' ? (
                    <div
                      className="mirrored-video-container"
                      style={{
                        width: 'auto',
                        maxWidth: '600px',
                        borderRadius: '8px',
                        position: 'relative',
                        display: 'inline-block',
                        // boxShadow: '0 10px 24px rgba(204, 180, 214, 0.25)'
                      }}
                    >
                      <video
                        ref={videoRef}
                        src={currentSlideData.content}
                        controls
                        preload="metadata"
                        playsInline
                        muted
                        autoPlay
                        loop
                        style={{
                          width: 'auto',
                          height: '375px',
                          maxHeight: '55vh',
                          borderRadius: '8px',
                          transform: 'scaleX(-1)', // Mirror the video content
                          transformOrigin: 'center',
                          backfaceVisibility: 'hidden' // Prevent flickering
                        }}
                        onLoadedData={(e) => {
                          // Ensure video starts playing
                          console.log('Video loaded data, attempting to start');
                          e.target.play().catch((error) => {
                            // Fallback if autoplay failed
                            console.log('Autoplay failed, user needs to click play:', error);
                          });
                        }}
                        onCanPlay={(e) => {
                          // Try to play when video can start playing
                          console.log('Video can play, attempting to start');
                          e.target.play().catch((error) => {
                            console.log('CanPlay autoplay failed:', error);
                          });
                        }}
                        onPlay={(e) => {
                          console.log('Video started playing successfully');
                        }}
                        onError={(e) => {
                          console.error('Video error:', e.target.error);
                        }}
                      />
                      {/* Add CSS to fix controls orientation and prevent flickering */}
                      <style dangerouslySetInnerHTML={{
                        __html: `
                            .mirrored-video-container video {
                              -webkit-backface-visibility: hidden;
                              backface-visibility: hidden;
                              -webkit-transform: translate3d(0, 0, 0);
                              transform: translate3d(0, 0, 0);
                              -webkit-filter: none;
                              filter: none;
                            }
                            .mirrored-video-container video::-webkit-media-controls-panel {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-play-button {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-mute-button {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-fullscreen-button {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-current-time-display {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-time-remaining-display {
                              transform: scaleX(-1) !important;
                            }
                          `
                      }} />
                    </div>
                  ) : currentSlideData.type === 'video' ? (
                    <div
                      className="mirrored-video-container"
                      style={{
                        width: '100%',
                        maxWidth: '600px',
                        borderRadius: '8px',
                        position: 'relative',
                        display: 'inline-block',
                        boxShadow: '0 10px 24px rgba(204, 180, 214, 0.25)'
                      }}
                    >
                      <video
                        src={currentSlideData.content}
                        controls
                        preload="metadata"
                        playsInline
                        muted
                        autoPlay
                        loop
                        onLoadedData={(e) => {
                          // Ensure video starts playing
                          if (e.target.paused) {
                            e.target.play().catch(() => {
                              // Fallback if autoplay fails
                              console.log('Autoplay failed, user needs to click play');
                            });
                          }
                        }}
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '55vh',
                          borderRadius: '8px',
                          transform: 'scaleX(-1)', // Mirror the video content
                          transformOrigin: 'center',
                          backfaceVisibility: 'hidden' // Prevent flickering
                        }}
                      />
                      {/* Add CSS to fix controls orientation and prevent flickering */}
                      <style dangerouslySetInnerHTML={{
                        __html: `
                            .mirrored-video-container video {
                              -webkit-backface-visibility: hidden;
                              backface-visibility: hidden;
                              -webkit-transform: translate3d(0, 0, 0);
                              transform: translate3d(0, 0, 0);
                              -webkit-filter: none;
                              filter: none;
                            }
                            .mirrored-video-container video::-webkit-media-controls-panel {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-play-button {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-mute-button {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-fullscreen-button {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-current-time-display {
                              transform: scaleX(-1) !important;
                            }
                            .mirrored-video-container video::-webkit-media-controls-time-remaining-display {
                              transform: scaleX(-1) !important;
                            }
                          `
                      }} />
                    </div>
                  ) : (
                    <div style={{
                      width: 'min(90vw, 400px)',
                      height: 'min(50vh, 300px)',
                      background: '#e9ecef',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6c757d',
                      fontSize: '48px'
                    }}>
                      📄
                    </div>
                  )}
                </div>
              </div>

            

              {/* Filename Display */}
              {/* <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '12px',
                color: '#495057',
                wordBreak: 'break-all'
              }}>
                {currentSlideData.filename}
              </div> */}

              {/* Slide Counter */}
              <div style={{
                textAlign: 'center',
                marginBottom: '15px',
                color: '#6c757d',
                fontSize: '14px'
              }}>
                {currentSlide + 1} / {totalSlides} - {currentSlideData.title}
              </div>

              {/* Slide Indicators */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '20px'
              }}>
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      border: 'none',
                      background: index === currentSlide ? '#ccb4d6' : '#e9ecef',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (index !== currentSlide) {
                        e.target.style.background = '#d9c7e3';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (index !== currentSlide) {
                        e.target.style.background = '#e9ecef';
                      }
                    }}
                  />
                ))}
              </div>

              {/* Download Button for Current Slide */}
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={async () => {
                    try {
                      if (currentSlideData.type === 'final-frame') {
                        await downloadImage();
                      } else if (currentSlideData.type === 'video-frame') {
                        await downloadVideoFrame();
                      } else {
                        downloadSessionFile(currentSlideData.filename);
                      }
                    } catch (error) {
                      console.error('Download failed:', error);
                      alert('Download failed. Please try again.');
                    }
                  }}
                  style={{
                    padding: '12px 22px',
                    background: '#ccb4d6',
                    color: '#3b2f46',
                    border: '1px solid rgba(204, 180, 214, 0.6)',
                    borderRadius: '10px',
                    cursor: videoDownloadLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    boxShadow: '0 10px 18px rgba(204, 180, 214, 0.35)',
                    transition: 'all 0.25s ease',
                    opacity: videoDownloadLoading ? 0.6 : 1
                  }}
                  disabled={videoDownloadLoading}
                  onMouseEnter={(e) => {
                    if (!videoDownloadLoading) {
                      e.target.style.background = '#bca0cd';
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!videoDownloadLoading) {
                      e.target.style.background = '#ccb4d6';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {videoDownloadLoading && currentSlideData.type === 'video-frame' ? 'Converting...' : `Download ${currentSlideData.type === 'final-frame' ? 'Photo' : currentSlideData.type === 'video-frame' ? 'Video' : currentSlideData.title}`}
                </button>
              </div>

              {/* Thumbnail Navigation */}
              <div style={{
                marginTop: '20px',
                display: 'flex',
                gap: '5px',
                overflowX: 'auto',
                paddingBottom: '10px'
              }}>
                {slides.map((slide, index) => (
                  <div
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    style={{
                      width: '60px',
                      height: '80px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: currentSlide === index ? '2px solid #ccb4d6' : '1px solid #e9ecef',
                      boxShadow: currentSlide === index ? '0 6px 16px rgba(204, 180, 214, 0.35)' : '0 2px 8px rgba(0,0,0,0.06)',
                      flexShrink: 0,
                      position: 'relative',
                      transition: 'all 0.25s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (currentSlide !== index) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(204, 180, 214, 0.25)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentSlide !== index) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                      }
                    }}
                  >
                    {slide.type === 'final-frame' ? (
                      <img
                        src={slide.content}
                        alt="Final"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : slide.type === 'photo' ? (
                      <img
                        src={slide.content}
                        alt="Photo"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transform: 'scaleX(-1)'
                        }}
                      />
                    ) : slide.type === 'video' ? (
                      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <video
                          src={slide.content}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transform: 'scaleX(-1)'
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%) scaleX(-1)',
                          color: '#fff',
                          fontSize: '12px'
                        }}>
                          ▶
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: '#C6B4D6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6c757d',
                        fontSize: '40px'
                      }}>
                        🎬
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {(!sessionData || !sessionData.files || sessionData.files.length === 0) && !loading && !error && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            textAlign: 'center',
            color: '#6c757d'
          }}>
            No files found for this session
          </div>
        )}
      </div>
    </div>
  );
};

export default FinalOutput;
